import { createCallchainHttp } from './callchain-http.js'

let http = null

export function configureIntentVideoHttp(options) {
  http = createCallchainHttp({ ...options, basePath: '/api/visual-game/admin' })
}

function client() {
  if (http === null) throw new Error('configureIntentVideoHttp must be called before intent video API use')
  return http
}

export async function listIntentVideos(query) {
  return client().get('/intent-videos', { query, routeId: 'visualGame.intentVideo.list' })
}

export async function listVideoPreparation(locale) {
  return client().get('/video-preparation', {
    query: { locale },
    routeId: 'visualGame.intentVideo.preparation',
  })
}

export async function listIntentVideoQueue(query) {
  return client().get('/intent-video-queue', { query, routeId: 'visualGame.intentVideo.queue' })
}

/** 意图聚类工作台：全部聚类 + 每条的已覆盖 / 已入队状态。状态由服务端算，不靠前端记。 */
export async function listIntentClusters(query) {
  return client().get('/intent-video-clusters', { query, routeId: 'visualGame.intentVideo.clusters' })
}

/** 把一条意图加进队列。同一条点两次只会累加 occurrences，不会排两遍。 */
export async function enqueueIntentVideo({ scenario_id: scenarioId, intent_text: intentText }) {
  return client().post('/intent-video-queue', {
    body: { scenario_id: scenarioId, intent_text: intentText },
    routeId: 'visualGame.intentVideo.enqueue',
  })
}

/** 按当前规范重写这条意图（解绑资源回队首）。和 retry 不是一件事。 */
export async function rePromptIntentVideoRequest(requestId) {
  return client().post(`/intent-video-queue/${encodeURIComponent(requestId)}/re-prompt`, {
    body: {},
    routeId: 'visualGame.intentVideo.rePrompt',
  })
}

export async function retryIntentVideoRequest(requestId) {
  return client().post(`/intent-video-queue/${encodeURIComponent(requestId)}/retry`, {
    body: {},
    routeId: 'visualGame.intentVideo.retry',
  })
}

/** 一键从 pending 走到出片：服务端自动认领、调 lemonboss 写提示词、直接触发出片。接口立刻返回，进度看队列状态。 */
export async function autoGenerateIntentVideoRequest(requestId) {
  return client().post(`/intent-video-queue/${encodeURIComponent(requestId)}/auto-generate`, {
    body: {},
    routeId: 'visualGame.intentVideo.autoGenerate',
  })
}

/**
 * 给一条还没写过提示词的队列行（pending/prompting）第一次写规格。
 * pending 会被服务端自动认领；建好资源后这条请求就转成 prompt_ready。
 */
export async function writeIntentVideoPrompt(requestId, spec) {
  return client().post(`/intent-video-queue/${encodeURIComponent(requestId)}/prompt`, {
    body: spec,
    routeId: 'visualGame.intentVideo.writePrompt',
  })
}

/**
 * 详情。生成队列和资源列表点开的是同一件事的两个入口，所以读同一个接口：
 * 队列那条传 request_id，资源那条传 resource_id，服务端把另一半补齐。
 */
export async function getIntentVideoDetail({ requestId = null, resourceId = null }) {
  return client().get('/intent-video-detail', {
    query: { request_id: requestId ?? undefined, resource_id: resourceId ?? undefined },
    routeId: 'visualGame.intentVideo.detail',
  })
}

/** 「任务日志」tab：一条队列请求的完整调用链（拼提示词 → 意图 embedding → H3 出片），按时间合并。 */
export async function getIntentVideoTaskLogs(requestId) {
  return client().get('/intent-video-task-logs', {
    query: { request_id: requestId },
    routeId: 'visualGame.intentVideo.taskLogs',
  })
}

/** 只读预览：这条请求现在触发生成会发给 Lemonboss 什么任务书，不调用 Lemonboss、不改状态。 */
export async function getIntentVideoTaskPreview(requestId) {
  return client().get(`/intent-video-queue/${encodeURIComponent(requestId)}/task-preview`, {
    routeId: 'visualGame.intentVideo.taskPreview',
  })
}

export async function listIntentVideoTaskSources(query) {
  return client().get('/intent-video-task-sources', {
    query,
    routeId: 'visualGame.intentVideo.taskSources',
  })
}

/** 改出片规格。只动「下一次按什么拍」，当前这条片子的来历留在出片记录里。 */
export async function updateIntentVideoSpec(resourceId, spec) {
  return client().put(`/intent-videos/${encodeURIComponent(resourceId)}/h3-spec`, {
    body: spec,
    routeId: 'visualGame.intentVideo.updateSpec',
  })
}

/**
 * 按当前规格重新出片。接口立刻返回，出片在服务端后台跑（实测 152~657 秒），
 * 进度看队列那一行的状态，所以调用方拿到 ok 之后要接着轮询详情。
 */
export async function renderIntentVideo(resourceId) {
  return client().post(`/intent-videos/${encodeURIComponent(resourceId)}/render`, {
    body: {},
    routeId: 'visualGame.intentVideo.render',
  })
}

export async function searchIntentVideos({ scenario_id: scenarioId, text, limit = 20 }) {
  return client().post('/intent-video-search', {
    body: { scenario_id: scenarioId ?? null, text, limit },
    routeId: 'visualGame.intentVideo.search',
  })
}

export async function disableIntentVideo(resourceId, reason = null) {
  return client().post(`/intent-videos/${encodeURIComponent(resourceId)}/disable`, {
    body: { reason },
    routeId: 'visualGame.intentVideo.disable',
  })
}

export async function enableIntentVideo(resourceId) {
  return client().post(`/intent-videos/${encodeURIComponent(resourceId)}/enable`, {
    body: {},
    routeId: 'visualGame.intentVideo.enable',
  })
}

export async function updateIntentVideoText(resourceId, intentText) {
  return client().put(`/intent-videos/${encodeURIComponent(resourceId)}/intent-text`, {
    body: { intent_text: intentText },
    routeId: 'visualGame.intentVideo.updateText',
  })
}

/** 换片。首尾帧由服务端从这条 mp4 现抽，前端只传视频本身。 */
export async function replaceIntentVideo(resourceId, { dataBase64, fileName, mime, note = null }) {
  return client().put(`/intent-videos/${encodeURIComponent(resourceId)}/replace`, {
    body: { data_base64: dataBase64, file_name: fileName, mime, note },
    routeId: 'visualGame.intentVideo.replace',
  })
}

/** H3 插队资格现状 + H3 真实排队深度，只读。 */
export async function getH3QueueStatus() {
  return client().get('/h3-queue-status', { routeId: 'visualGame.intentVideo.h3QueueStatus' })
}
